/*
 * Copyright (c) 2019 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import { DataTable } from '@scuf/datatable';
import { util, NotificationHeaderModal } from '@keystone/common';
import { Icon, Grid, Search, Loader, Button, Modal, Badge } from '@scuf/common';
import * as React from 'react';
import {
  withDeviceInfoContext,
  withApplicationContext,
  withCustomerSiteContext,
} from '../../common/HOCs';

import AppModal from '../../partials/AppModal';

class DisplayDeviceInfo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      devices: null,
      deviceHistory: null,
      loadingSettings: false,
      disableMultipleDelete: true,
      historyOpen: false, // this variable is to check if the history modal is open
      logOpen: false, // this variable is to check if the log modal is open
      logs: null,
    };
  }

  componentDidMount() {
    const { appContext } = this.props;
    appContext.registerReloadFunction(this.reloadTableComponent);
    this.initializeTable();
    appContext.setNotification(null);
  }

  toggleMultipleDelete = (e) => {
    if (e.length > 0) {
      this.setState({ disableMultipleDelete: false });
    } else {
      this.setState({ disableMultipleDelete: true });
    }
  };

  disableMultipleDelete = () => {
    this.props.deviceInfoContext.toggleConfirmDeleteModal();
    this.props.deviceInfoContext.clearSelectedDevices();
    this.setState({ disableMultipleDelete: true });
  };

  reloadTableComponent = async (signal) => {
    const devices = await this.props.deviceInfoContext.fetchDevices(signal);
    this.setState({ devices });
  };

  componentWillUnmount() {
    const { appContext } = this.props;
    appContext.unregisterReloadFunction();
  }

  initializeTable = async () => {
    this.setState({ loadingSettings: true });
    await this.reloadTableComponent();
    this.setState({ loadingSettings: false });
  };

  handleRowClick = async (props) => {
    this.setState({ deviceHistory: null, loadingSettings: true });
    const { serialNumber } = props;
    await this.props.deviceInfoContext.fetchDeviceHistory(serialNumber);
    this.setState({
      deviceHistory: this.props.deviceInfoContext.deviceHistory,
    });
    this.setState({ historyOpen: true, loadingSettings: false });
  };

  userActionIcons = (props) => {
    return (
      <div>
        <a onClick={() => this.handleRowClick(props)}>
          <Icon name="badge-info" size="large" />
        </a>
      </div>
    );
  };

  handleLogRowClick = async (props) => {
    this.setState({ logs: null, loadingSettings: true });
    const { serialNumber } = props;
    await this.props.deviceInfoContext.fetchLogs(serialNumber);
    this.setState({
      logs: this.props.deviceInfoContext.logs,
    });
    this.setState({ logOpen: true, loadingSettings: false });
  };

  logIcons = (props) => {
    return (
      <div>
        <a onClick={() => this.handleLogRowClick(props)}>
          <Icon name="document" size="large" />
        </a>
      </div>
    );
  };

  buttonTemplate = (props) => {
    return (
      <a
        onClick={() => null}
        href={
          `${util.getAPIGatewayURL()}/logs/downloadProblemLogs` +
          `?id_token=${this.props.deviceInfoContext.getIdToken()}` +
          `&selectedFile=${props.parentDir}%2F${props.fileName}` +
          `&selectedClientId=${this.props.custSiteContext.selectedCustomer}`
        }
      >
        <Icon name="file-download" size="large" />
      </a>
    );
  };

  handleMultipleDeleteButton = () => {
    const { appContext } = this.props;
    this.props.deviceInfoContext.deleteSelectedDevices();
    this.setState({ disableMultipleDelete: true });
    appContext.reload();
  };

  statusRenderer = (props) => {
    const color = props.leaseAvailTime === '' ? 'red' : 'green';
    const status = props.leaseAvailTime === '' ? 'offline' : 'online';
    return (
      <div>
        <a>
          <Badge color={color}>{status}</Badge>
        </a>
      </div>
    );
  };

  renderDeleteModal() {
    return (
      <AppModal
        isOpen={this.props.deviceInfoContext.isConfirmDeleteModalOpen}
        content="Would you like to delete the selected devices?"
        handleSubmit={() => this.handleMultipleDeleteButton()}
        handleToggle={() => this.disableMultipleDelete()}
      />
    );
  }

  renderActionButtons = () => {
    const { appContext, deviceInfoContext } = this.props;
    const { disableMultipleDelete } = this.state;
    if (util.getSentienceEnableFlag() || appContext.isAdmin) {
      return (
        <DataTable.HeaderBar>
          <DataTable.HeaderBar.Item
            content="Delete"
            icon={<Icon name="delete" size="small" root="common" />}
            disabled={disableMultipleDelete}
            onClick={() => deviceInfoContext.toggleConfirmDeleteModal()}
          />
          <DataTable.HeaderBar.Item content="" />
        </DataTable.HeaderBar>
      );
    }
    return <></>;
  };

  render() {
    return (
      <div style={{ position: 'relative', height: '100%' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={12}>
              {this.renderModal()}
              {this.renderLogModal()}
              {this.renderDeleteModal()}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <h3 />
              <Loader
                loading={this.state.loadingSettings}
                overlayOpacity={0.2}
                overlayColor="#B2AFAF"
              >
                <div className="placeholder" />
              </Loader>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={12}>
              <DataTable
                data={
                  this.state.devices === null
                    ? []
                    : this.state.devices === undefined
                    ? []
                    : this.state.devices
                }
                resizableColumns
                columnResizeMode="fit"
                reorderableColumns={false}
                selection={this.props.deviceInfoContext.selectedRows}
                onSelectionChange={(e) => {
                  this.props.deviceInfoContext.setSelectedRow(e);
                  this.toggleMultipleDelete(e);
                }}
                metaKeySelection={false}
                scrollable
                search
              >
                {this.renderActionButtons()}
                <DataTable.Column selectionMode="multiple" initialWidth={5} />
                <DataTable.Column
                  field="serialNumber"
                  header="Device ID"
                  sortable
                  initialWidth={20}
                />
                <DataTable.Column field="product" header="Product" sortable initialWidth={5} />
                <DataTable.Column
                  field="productVersion"
                  header="Version"
                  sortable
                  initialWidth={5}
                />
                <DataTable.Column
                  field="buildInfo"
                  header="Build"
                  sortable
                  initialWidth={10}
                />
                <DataTable.Column
                  field="leaseAvailTime"
                  header="Lease Issue Time"
                  sortable
                  initialWidth={15}
                />
                <DataTable.Column
                  field="leaseReleaseTime"
                  header="Lease Release Time"
                  sortable
                  initialWidth={15}
                />
                <DataTable.Column
                  field="serialNumber"
                  header="History"
                  sortable={false}
                  initialWidth={5}
                  renderer={(props) => this.userActionIcons(props.rowData)}
                />
                <DataTable.Column
                  field="serialNumber"
                  header="Logs"
                  sortable={false}
                  initialWidth={5}
                  renderer={(props) => this.logIcons(props.rowData)}
                />
                <DataTable.Pagination
                  itemsPerPage={10}
                  totalItems={
                    this.state.devices === null
                      ? 1
                      : this.state.devices === undefined
                      ? 1
                      : this.state.devices.length
                  }
                  showDisplayDetails
                />
              </DataTable>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    );
  }

  renderModal() {
    return (
      <Modal
        closeIcon
        onClose={() => this.setState({ historyOpen: false })}
        open={this.state.historyOpen}
        dimmer={this.state.hasDimmer}
        closeOnDimmerClick={false}
      >
        <Modal.Header>
          Device History (
          {this.props.deviceInfoContext.selectedDeviceSerialNumber === null
            ? ''
            : this.props.deviceInfoContext.selectedDeviceSerialNumber}
          )
        </Modal.Header>
        <Modal.Content>
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <DataTable
                  data={
                    this.state.deviceHistory === null
                      ? []
                      : this.state.deviceHistory === undefined
                      ? []
                      : this.state.deviceHistory
                  }
                  resizableColumns
                  columnResizeMode="fit"
                  reorderableColumns={false}
                  scrollable
                  search
                >
                  <DataTable.Column field="product" header="Product" sortable initialWidth={15} />
                  <DataTable.Column
                    field="productVersion"
                    header="Product Version"
                    sortable
                    initialWidth={20}
                  />
                  <DataTable.Column
                    field="leaseAvailTime"
                    header="Lease Issue Time"
                    sortable
                    initialWidth={20}
                  />
                  <DataTable.Column
                    field="leaseReleaseTime"
                    header="Lease Release Time"
                    sortable
                    initialWidth={25}
                  />
                  <DataTable.Pagination
                    itemsPerPage={10}
                    totalItems={
                      this.state.deviceHistory === null
                        ? 1
                        : this.state.deviceHistory === undefined
                        ? 1
                        : this.state.deviceHistory.length
                    }
                    showDisplayDetails
                  />
                </DataTable>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Footer>
          <Button
            type="secondary"
            size="medium"
            content="Cancel"
            onClick={() => this.setState({ historyOpen: false })}
          />
        </Modal.Footer>
      </Modal>
    );
  }

  renderLogModal() {
    return (
      <Modal
        closeIcon
        onClose={() => this.setState({ logOpen: false })}
        open={this.state.logOpen}
        dimmer={this.state.hasDimmer}
        closeOnDimmerClick={false}
      >
        <Modal.Header>Logs</Modal.Header>
        <Modal.Content>
          {this.props.appContext.notificationModal.hasNotification ? (
            <NotificationHeaderModal context={this.props.appContext} />
          ) : (
            <></>
          )}
          <Grid>
            <Grid.Row>
              <Grid.Column width={12}>
                <DataTable
                  data={
                    this.state.logs === null
                      ? []
                      : this.state.logs === undefined
                      ? []
                      : this.state.logs
                  }
                  resizableColumns
                  columnResizeMode="fit"
                  reorderableColumns={false}
                  metaKeySelection={false}
                  scrollable
                  search
                >
                  <DataTable.Column
                    field="operatorID"
                    header="Operator"
                    sortable={false}
                    initialWidth={20}
                  />
                  <DataTable.Column field="fileName" header="Log Name" sortable initialWidth={20} />
                  <DataTable.Column
                    field="fileCreatedDate"
                    header="Recieved"
                    sortable
                    initialWidth={20}
                  />
                  <DataTable.Column
                    field="displayFileSize"
                    header="Size"
                    sortable
                    initialWidth={20}
                  />
                  <DataTable.Column
                    field="actualFileSize"
                    header="Download"
                    initialWidth={20}
                    renderer={(props) => this.buttonTemplate(props.rowData)}
                  />
                  <DataTable.Pagination
                    itemsPerPage={10}
                    totalItems={
                      this.state.logs === null
                        ? 0
                        : this.state.logs === undefined
                        ? 0
                        : this.state.logs.length
                    }
                    showDisplayDetails
                  />
                </DataTable>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Modal.Content>
        <Modal.Footer>
          <Button
            type="secondary"
            size="medium"
            content="Cancel"
            onClick={() => this.setState({ logOpen: false })}
          />
        </Modal.Footer>
      </Modal>
    );
  }
}

export default withApplicationContext(
  withCustomerSiteContext(withDeviceInfoContext(DisplayDeviceInfo))
);
